import { authenticationApi } from "apis";
import { auth } from "config/firebase.config";
import { SecureStorageKeys } from "constants/secureStorage";
import { ChatSocketProvider } from "contexts/ChatSocketContext";
import { signInWithCustomToken } from "firebase/auth";
import { GoogleSignInRequestUserTypeEnum } from "libs/cellera-sdk";
import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router";
import { secureStorage } from "utils/secureStorage";

import { EPath } from "../constants/path";
import { SubscriptionStatus } from "../constants/payment";
import { useAuth } from "../contexts/AuthContext";
import { usePaymentStatus } from "../hooks/usePaymentStatus";
import { RouteConfig } from "./routeConfig";

interface ProtectedRouteProps {
  route: RouteConfig;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ route }) => {
  const { user, userClaims, loading } = useAuth();
  const location = useLocation();
  const [isRefreshingToken, setIsRefreshingToken] = useState(false);
  const Element = route.element;

  // Fetch subscription status to verify Stripe payment success
  const { data: subscriptionStatus, isLoading: isLoadingPayment } =
    usePaymentStatus();
  const isPaymentActive =
    subscriptionStatus?.status === SubscriptionStatus.ACTIVE;

  // Auto-refresh userClaims when subscription is active but user still needs onboarding
  // This handles the case when payment succeeds and requiresOnboarding should become false
  const refreshUserTokenAndClaims = async () => {
    try {
      setIsRefreshingToken(true);
      const currentUser = auth?.currentUser;
      if (!currentUser) return;

      const idToken = await currentUser.getIdToken(true);

      const response = await authenticationApi.googleSignIn({
        googleSignInRequest: {
          idToken,
          userType: GoogleSignInRequestUserTypeEnum.Provider,
          platform: "web",
        },
      });

      // set custom token to local storage
      if (response.customToken) {
        const userCred = await signInWithCustomToken(
          auth,
          response.customToken
        );
        const idToken = await userCred.user.getIdToken();
        secureStorage.set(SecureStorageKeys.CUSTOM_TOKEN, idToken);
        window.location.reload();
      }
    } catch (error: unknown) {
      console.error("Error refreshing token: ", error);
    } finally {
      setIsRefreshingToken(false);
    }
  };

  useEffect(() => {
    const processRefreshUserTokenAndClaims = async () => {
      if (isPaymentActive && userClaims?.requiresOnboarding)
        await refreshUserTokenAndClaims();
    };
    void processRefreshUserTokenAndClaims();
  }, [isPaymentActive, userClaims?.requiresOnboarding]);

  // Show loading while checking authentication or payment status
  if (loading || isLoadingPayment || isRefreshingToken) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <div className="text-gray-600">Loading...</div>
        </div>
      </div>
    );
  }

  // If route is not protected, render directly
  if (!route.protected) {
    return <Element />;
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to={EPath.LOGIN} state={{ from: location }} replace />;
  }

  // Handle onboarding route
  if (
    route.requireOnboarding &&
    !userClaims?.requiresOnboarding &&
    isPaymentActive
  ) {
    return <Element />;
  }

  if (
    !route.requireOnboarding &&
    userClaims?.requiresOnboarding === true &&
    !isPaymentActive
  ) {
    return <Navigate to={EPath.ONBOARDING} replace />;
  }

  // User is authenticated and has completed onboarding (or onboarding is not required)
  return (
    <ChatSocketProvider>
      <Element />
    </ChatSocketProvider>
  );
};

export default ProtectedRoute;
